import React from 'react';
import { StyleSheet, View } from 'react-native';

import { AvatarIcon, UserAvatar } from '@components/Avatar';
import Icon from '@components/Icon';
import { PressableOpacity } from '@components/Pressable';
import Surface from '@components/Surface';
import { Caption, Paragraph } from '@components/Typography';
import FollowButton from '@components/User/FollowButton';
import MessageButton from '@components/User/MessageButton';
import { useFollowing } from '@context/FollowingContext';
import { useAppTheme } from '@context/ThemeContext';
import { dateRangeToString } from '@helpers/Dates';
import { getCurrentUserId } from '@helpers/firebase/Auth';
import { User } from '@types';
import { JoinFeedUser, JoinFeedUserType } from '@types';

const LARGE_AVATAR_SIZE = 50;
const SMALL_AVATAR_SIZE = 50;
const ICON_SIZE = 20;

export type UserData = Pick<
  User,
  'userId' | 'displayName' | 'photoUrl' | 'username' | 'numFollowers'
> & {
  hometown?: Pick<User['hometown'], 'displayName'>;
};

export type UserCardProps<T extends UserData> = {
  data: T;
  onPress: (user: T) => any;
  caption?: CaptionProps;
  messageButton?: {
    onPress: React.ComponentProps<typeof MessageButton>['onPress'];
  };
} & Omit<React.ComponentPropsWithoutRef<typeof Surface>, 'children'>;

export function LargeUserCard<T extends UserData>({
  data,
  style,
  onPress,
  caption,
  messageButton,
  ...props
}: UserCardProps<T>) {
  const following = useFollowing();
  const appTheme = useAppTheme();
  const isCurrentUser = getCurrentUserId() === data.userId;
  const isFollowing = following?.followingUserIds.has(data.userId) ?? false;
  const showFollowButton = !isCurrentUser && !isFollowing;
  const showMessageButton = !isCurrentUser && isFollowing && Boolean(messageButton);

  const { captionText, captionIcon } = caption || getCaption(data);

  return (
    <PressableOpacity testID={data.userId} onPress={() => onPress(data)}>
      <Surface
        style={[
          styles.container,
          largeStyles.container,
          { maxWidth: appTheme.maxWidth },
          style,
          { elevation: undefined },
        ]}
        {...props}>
        <View style={[styles.row, largeStyles.full]}>
          <UserAvatar
            displayName={data.displayName}
            photoUrl={data.photoUrl}
            size={LARGE_AVATAR_SIZE}
            style={styles.avatar}
          />
          <View style={[largeStyles.column, largeStyles.expanding]}>
            <Paragraph numberOfLines={1} style={styles.text}>
              {data.displayName}
            </Paragraph>
            <View style={styles.row}>
              <Icon name={captionIcon} size={ICON_SIZE} style={largeStyles.icon} />
              <Caption numberOfLines={1} style={styles.text}>
                {captionText}
              </Caption>
            </View>
          </View>
          {showFollowButton ? (
            <FollowButton
              userId={data.userId}
              style={largeStyles.actionButton}
              labelStyle={largeStyles.actionButtonText}
            />
          ) : null}
          {showMessageButton && messageButton ? (
            <MessageButton
              userDoc={data}
              style={largeStyles.actionButton}
              labelStyle={largeStyles.actionButtonText}
              onPress={messageButton.onPress}
            />
          ) : null}
        </View>
      </Surface>
    </PressableOpacity>
  );
}

export function SmallUserCard<T extends UserData>({
  data,
  style,
  onPress,
  caption,
  messageButton,
  ...props
}: UserCardProps<T>) {
  const following = useFollowing();
  const isCurrentUser = getCurrentUserId() === data.userId;
  const isFollowing = following?.followingUserIds.has(data.userId) ?? false;
  const showFollowButton = !isCurrentUser && !isFollowing;
  const showMessageButton = !isCurrentUser && isFollowing && Boolean(messageButton);

  const { captionText, captionIcon } = caption || getCaption(data);

  return (
    <PressableOpacity testID={data.userId} onPress={() => onPress(data)}>
      <Surface style={[styles.container, smallStyles.container, style]} {...props}>
        <UserAvatar
          displayName={data.displayName}
          photoUrl={data.photoUrl}
          size={SMALL_AVATAR_SIZE}
          style={styles.avatar}
        />
        <Paragraph numberOfLines={1} style={styles.text}>
          {data.displayName}
        </Paragraph>
        <View style={[styles.row, smallStyles.captionContainer]}>
          <Icon name={captionIcon} size={ICON_SIZE} style={smallStyles.icon} />
          <Caption numberOfLines={1} style={styles.text}>
            {captionText}
          </Caption>
        </View>
        {showFollowButton ? (
          <FollowButton
            userId={data.userId}
            style={smallStyles.button}
            labelStyle={smallStyles.buttonLabel}
          />
        ) : null}
        {showMessageButton && messageButton ? (
          <MessageButton
            userDoc={data}
            onPress={messageButton.onPress}
            style={smallStyles.button}
            labelStyle={smallStyles.buttonLabel}
          />
        ) : null}
      </Surface>
    </PressableOpacity>
  );
}

type SmallUserCardPlaceholderProps = {
  mainText: string;
  icon?: React.ComponentProps<typeof AvatarIcon>['icon'];
  secondaryText?: string;
  onPress?: () => any;
  style?: React.ComponentProps<typeof Surface>['style'];
  pressableStyle?: React.ComponentProps<typeof PressableOpacity>['style'];
};

export function SmallUserCardPlaceholder({
  mainText,
  icon = 'account',
  secondaryText,
  onPress,
  style,
  pressableStyle,
  ...props
}: SmallUserCardPlaceholderProps) {
  return (
    <PressableOpacity onPress={onPress} style={pressableStyle}>
      <Surface style={[styles.container, smallStyles.container, style]} {...props}>
        <AvatarIcon icon={icon} size={SMALL_AVATAR_SIZE} style={styles.avatar} />
        <Paragraph numberOfLines={1} style={styles.text}>
          {mainText}
        </Paragraph>
        {secondaryText ? (
          <Caption numberOfLines={1} style={styles.text}>
            {secondaryText}
          </Caption>
        ) : null}
      </Surface>
    </PressableOpacity>
  );
}

type CaptionProps = {
  captionIcon: React.ComponentProps<typeof Icon>['name'];
  captionText: string;
};

function getCaption<T extends UserData>(userDoc: T): CaptionProps {
  if (isJoinFeedUser(userDoc)) {
    return getJoinFeedCaption(userDoc);
  } else {
    return getDefaultCaption(userDoc);
  }
}

function getDefaultCaption<T extends UserData>(userDoc: T): CaptionProps {
  if (!userDoc.hometown?.displayName) {
    return {
      captionIcon: 'at',
      captionText: userDoc.username,
    };
  }

  return {
    captionIcon: 'home-outline',
    captionText: userDoc.hometown.displayName,
  };
}

function getJoinFeedCaption(user: JoinFeedUser): CaptionProps {
  switch (user.joinFeedType) {
    case JoinFeedUserType.RESIDENT:
      return {
        captionIcon: 'home-outline',
        captionText: 'Resident',
      };
    case JoinFeedUserType.CURRENT_TRAVELER:
      return {
        captionIcon: 'sign-direction',
        captionText: user.travelInfo
          ? dateRangeToString(
              {
                startDate: user.travelInfo.startDate,
                endDate: user.travelInfo.endDate,
              },
              'M/d'
            )
          : 'Traveler',
      };
    case JoinFeedUserType.PAST_TRAVELER:
      return {
        captionIcon: 'pin-outline',
        captionText: 'Visited',
      };
  }
}

function isJoinFeedUser<T extends UserData>(userDoc: T | JoinFeedUser): userDoc is JoinFeedUser {
  if ((userDoc as JoinFeedUser).joinFeedType) {
    return true;
  } else {
    return false;
  }
}

const styles = StyleSheet.create({
  avatar: {
    margin: 5,
  },
  container: {
    alignItems: 'center',
    margin: 5,
    padding: 5,
  },
  row: {
    alignItems: 'center',
    flexDirection: 'row',
  },
  text: {
    flexShrink: 1,
  },
});

const largeStyles = StyleSheet.create({
  actionButton: {
    margin: 5,
    width: 100,
  },
  actionButtonText: {
    fontSize: 12,
  },
  column: {
    flex: 1,
    padding: 5,
  },
  container: {
    alignItems: 'flex-start',
    minWidth: 250,
  },
  expanding: {
    flex: 1,
    flexGrow: 1,
  },
  full: {
    flex: 1,
    width: '100%',
  },
  icon: {
    paddingRight: 5,
  },
});

const smallStyles = StyleSheet.create({
  button: {
    marginVertical: 5,
    width: '100%',
  },
  buttonLabel: {
    fontWeight: 'normal',
  },
  captionContainer: {
    justifyContent: 'center',
  },
  container: {
    padding: 10,
    width: 125,
  },
  icon: {
    paddingRight: 5,
  },
});
